import { useAuth } from '@auth/useAuth'
import { useEffect } from 'react'
import { identifyGainsightPx } from './gainsightPx'
import { useCurrentMember } from './useCurrentMember'
import { useCurrentTenant } from './useCurrentTenant'

/**
 * Non-rendering component that pushes the currently signed-in user/account
 * over to Gainsight PX so events tracked by the page tag are attributed to
 * the right user. The underlying `identifyGainsightPx` helper is a no-op
 * outside the production deploy, so this component is safe to mount in all
 * environments.
 *
 * Must be mounted inside `<AuthProvider>` and `<QueryClientProvider>` so the
 * underlying hooks have access to the auth context and the React Query cache.
 *
 * SYN-6649: FE: Add support for Gainsight PX overlay
 */
const GainsightPxIdentifier: React.FC = () => {
  const { isAuthenticated, isTenantScope } = useAuth()
  const [tenant] = useCurrentTenant()
  const { member } = useCurrentMember()

  const tenantMemberId = member?.id
  const tenantMemberCreatedAt = member?.createdAt
  const memberRole = member?.role
  const tenantAccountNumber = tenant?.accountNumber
  const tenantName = tenant?.name
  const tenantLicenseType = tenant?.licenseType

  useEffect(() => {
    if (!isAuthenticated || !isTenantScope) return
    if (!tenantMemberId || !tenantAccountNumber) return

    identifyGainsightPx(
      {
        id: tenantMemberId,
        role: memberRole,
        createdAt: tenantMemberCreatedAt,
      },
      {
        id: tenantAccountNumber,
        name: tenantName,
        licenseType: tenantLicenseType,
      },
    )
  }, [
    isAuthenticated,
    isTenantScope,
    tenantMemberId,
    tenantMemberCreatedAt,
    memberRole,
    tenantAccountNumber,
    tenantName,
    tenantLicenseType,
  ])

  return null
}

export default GainsightPxIdentifier
